import { AbstractDownloadableService } from "./abstract-downloadable.service";
import { HouseAccountTransaction } from '../models';
import { mapGetToPostParams } from '../../app/helpers';

/**
 * @class HouseAccountTransactionService
 * @abstract
 * @extends {AbstractDownloadableService<HouseAccountTransaction>}
 */
export class HouseAccountTransactionService extends AbstractDownloadableService {

    /**
     * @type {string}
     * @protected
     */
    get urlPattern() {

        return '/property/:propertyId/house-account(/:houseAccountId)/transaction(/:transactionId)';
    }

    /**
     * @param {Object.<string, *>} attributes
     * @returns {HouseAccountTransaction}
     * @protected
     */
    newModel(attributes, ...params) {

        return new HouseAccountTransaction(attributes);
    }

    newTransactionCollection(paginationData, ...params) {

        return {
            total_row: paginationData.total_row,
            models: Object.values(paginationData.data).map(groups => {

                return {
                    'total_row': groups.total_row,
                    'items': groups.items.map(attributes => this.newModel(attributes, ...params))
                };
            })
        };
    }

    /**
     * @param {...*} params
     * @return {function(response: Object): PaginationCollection<M>}
     * @protected
     */
    getTransactionCollector(...params) {

        return response => {

            const responseData = this.extractData(response);

            if (responseData.data && Array.isArray(responseData.data)) {

                responseData.data = {
                    group: {
                        items: responseData.data
                    }
                };
            }

            return this.newTransactionCollection(responseData, ...params);
        };
    }

    /**
     * @param {Object=} params
     * @param {HouseAccount=} houseAccount
     * @returns {Promise.<PaginationCollection<HouseAccountTransaction>>}
     */
    get (params = {}, houseAccount = null) {

        const url = this.url({
            propertyId: houseAccount ? houseAccount.property_id : 0,
            houseAccountId: houseAccount ? houseAccount.id : 0,
        });

        return this.http
            .post(url, params, {
                headers: this.authHeaders,
            })
            .then(this.getTransactionCollector())
            .catch(this.getErrorHandler());
    }

    /**
     * @param {HouseAccountTransaction} transaction
     * @param houseAccount
     * @returns {Promise.<boolean>}
     */
    delete(transaction, houseAccount) {

        const url = this.url({
            propertyId: houseAccount.property_id,
            houseAccountId: houseAccount.id,
            transactionId: transaction.id,
        });

        return this.http
            .delete(url, {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

    /**
     * @param {Object} payment
     * @param {HouseAccount} houseAccount
     * @returns {Promise.<boolean>}
     */
    addPayment(payment, houseAccount) {

        return this.addTransaction(payment, houseAccount, 'add-payment');
    }

    /**
     * @param {Object} payment
     * @param {HouseAccount} houseAccount
     * @returns {Promise.<boolean>}
     */
    refundPayment(payment, houseAccount) {

        return this.addTransaction(payment, houseAccount, 'refund-payment');
    }

    /**
     * @param {Object} transaction
     * @param {HouseAccount} houseAccount
     * @returns {Promise.<boolean>}
     */
    addItem(transaction, houseAccount) {

        transaction.item_id = transaction.item.id;
        delete transaction.item;

        return this.addTransaction(transaction, houseAccount, 'add-item');
    }

    addTaxFee(transaction, houseAccount) {

        transaction.tax_fee_id = transaction.tax_fee.id;
        delete transaction.tax_fee;

        return this.addTransaction(transaction, houseAccount, 'add-tax-fee');
    }

    addRoomRevenue(transaction, houseAccount) {

        transaction.taxes_fees = transaction.taxes_fees.map(({id}) => id);

        return this.addTransaction(transaction, houseAccount, 'add-room-revenue');
    }

    /**
     * @param {Object} transaction
     * @param {HouseAccount} houseAccount
     * @returns {Promise.<boolean>}
     */
    adjustCharge(transaction, houseAccount) {

        return this.addTransaction(transaction, houseAccount, 'adjust-charge');
    }

    addTransaction(transaction, houseAccount, path = null) {

        const urlParams = {
            propertyId: houseAccount.property_id,
            houseAccountId: houseAccount.id,
        };

        if (path) {

            urlParams['transactionId'] = path;
        }

        const url = this.url(urlParams);

        return this.http
            .post(url, transaction, {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

    defaultTaxFee(houseAccount) {

        const urlParams = {
            propertyId: houseAccount.property_id,
            houseAccountId: houseAccount.id,
            transactionId: 'default-taxes-fees',
        };

        const url = this.url(urlParams);

        return this.http
            .get(url, {
                headers: this.authHeaders
            })
            .then(this.getDataExtractor())
            .catch(this.getErrorHandler());
    }

    suggested(houseAccount) {

        const urlParams = {
            propertyId: houseAccount.property_id,
            houseAccountId: houseAccount.id,
            transactionId: 'suggested',
        };

        const url = this.url(urlParams);

        const adjustmentTypeMapper = {
            'App\\Item': 'item',
            'App\\Addon': 'item',
            'App\\TaxFee': 'tax-fee',
            'room-revenue': 'room-revenue',
        };

        const mapSuggested = models => models.map(({adjustment_id, adjustment_type}) => {

            return {
                id: adjustment_id,
                adjustment_type: adjustmentTypeMapper[adjustment_type]
            };
        });

        return this.http
            .get(url, {
                headers: this.authHeaders
            })
            .then(this.getDataExtractor())
            .then(mapSuggested)
            .catch(this.getErrorHandler());
    }

    exportPDF(params = {}, houseAccount) {

        const url = this.url({
            propertyId: houseAccount.property_id,
            houseAccountId: houseAccount.id,
        }) + '/export-pdf';

        return this.download(url, params, 'House Account Transactions.pdf');
    }

    exportExcel(params = {}, houseAccount) {

        const url = this.url({
            propertyId: houseAccount.property_id,
            houseAccountId: houseAccount.id,
        }) + '/export-excel';

        return this.download(url, params, 'House Account Transactions.xls');
    }
}



// WEBPACK FOOTER //
// ./src/pms-api/services/house-account-transaction.service.js